import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Divider, Button } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MapLayout from '../../styles/MapLayout';
import geojsonInnerBbox from '../../utils/geojsonInnerBbox';
import ZipCodeSelectionWidget from './maps/ZipCodeSelectionWidget';
import { UseStores } from '../../hooks/UseStores';
import { US_BBOX, ABDM_MIN_ADDRESSES } from '../../utils/constants';
import { scrollToSection } from '../../utils/abdmUtils';
import {
  fetchMarketBoundary,
  fetchQuote,
  fetchDemographics,
} from '../../utils/api';

const EMPTY_QUOTE = { isFetching: false, isError: false, result: null };
const FETCHING_QUOTE = { isFetching: true, isError: false, result: null };
const ERROR_QUOTE = { isFetching: false, isError: true, result: null };

const AreaSelector = () => {
  const { audience, mapstore } = UseStores();

  // data
  const [demographics, setDemographics] = useState({}); // Record<Prodcode, {isLoaded: boolean, result: Demographics[]}>
  const [selectedZipcodes, setSelectedZipcodes] = useState({}); //Record<zipcode, Demographic>
  const [quote, setQuote] = useState(EMPTY_QUOTE); //{isFetching: boolean, isError: boolean result: API response}
  const [marketBboxes, setMarketBboxes] = useState({
    '': {
      isLoaded: true,
      result: US_BBOX,
      limitZoom: false,
    },
  }); // Record<Prodcode, {isLoaded: boolean, result: [[lat, lng], [lat, lng]]}>

  const [newMarkets, setNewMarkets] = useState(
    audience.markets.slice(0, 20) || [],
  );

  const [isDisabled, setIsDisabled] = useState(true);
  const [prodcode, setProdcode] = useState(audience.markets[0]?.prodcode ?? '');
  const [prodcodeToZoom, setProdcodeToZoom] = useState(prodcode);

  const handleSelectMarketInOverview = useCallback(
    (prodcode) => {
      setProdcode(prodcode);
      setProdcodeToZoom(prodcode);
    },
    [prodcode],
  );

  const handleSelectMarket = useCallback(
    (prodcode, prodcodeToZoom) => {
      if (prodcode) {
        const market = newMarkets.find(
          (market) => market.prodcode === prodcode,
        );
        if (!market) {
          const initialMarket = audience.markets.find(
            (market) => market.prodcode === prodcode,
          );
          setNewMarkets([...newMarkets, initialMarket]);
        }
      }
      setProdcode(prodcode);
      setProdcodeToZoom(prodcodeToZoom);
    },
    [newMarkets],
  );

  useEffect(() => {
    if (!prodcodeToZoom || marketBboxes[prodcodeToZoom]) {
      return;
    }

    setMarketBboxes({
      ...marketBboxes,
      [prodcodeToZoom]: {
        isLoaded: false,
        result: null,
        limitZoom: true,
      },
    });
    fetchMarketBoundary(prodcodeToZoom)
      .then((geo) => {
        setMarketBboxes({
          ...marketBboxes,
          [prodcodeToZoom]: {
            isLoaded: true,
            result: geojsonInnerBbox(geo),
            limitZoom: true,
          },
        });
      })
      .catch(() =>
        setMarketBboxes({
          ...marketBboxes,
          [prodcodeToZoom]: { isLoaded: true, result: null, limitZoom: true },
        }),
      );
  }, [prodcodeToZoom, marketBboxes]);

  useEffect(() => {
    mapstore.initalizeMapStore();
    if (!selectedZipcodes) return;

    const zipcodes = Object.keys(selectedZipcodes);
    if (!zipcodes.length) {
      setQuote(EMPTY_QUOTE);
      return;
    }

    let isCancelled = false;
    setQuote(FETCHING_QUOTE);

    fetchQuote(audience.selectedAudience.label, zipcodes)
      .then((data) => {
        if (isCancelled) {
          return;
        }
        setQuote({ isFetching: false, isError: false, result: data });
        mapstore.updateData(data, selectedZipcodes);
      })
      .catch(() => {
        setQuote(ERROR_QUOTE);
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedZipcodes, audience.selectedAudience.label]);

  useEffect(() => {
    if (!prodcode || demographics[prodcode]) {
      return;
    }

    setDemographics({
      ...demographics,
      [prodcode]: {
        isLoaded: false,
        result: null,
      },
    });

    fetchDemographics(audience.selectedAudience.label, prodcode)
      .then((demographics) =>
        setDemographics({
          ...demographics,
          [prodcode]: { isLoaded: true, result: demographics },
        }),
      )
      .catch(() =>
        setDemographics({
          ...demographics,
          [prodcode]: { isLoaded: true, result: null },
        }),
      );
  }, [audience.selectedAudience.label, prodcode, demographics]);

  const currentDemogrData = demographics[prodcode]?.result;
  const onSelectFeature = useCallback(
    (zipcode, isSelected) => {
      setSelectedZipcodes((selectedZipcodes) => {
        const newZipcodes = { ...selectedZipcodes };
        const isCurrentlySelected = !!newZipcodes[zipcode];
        if (isSelected === undefined) {
          isSelected = !isCurrentlySelected;
        }
        if (isCurrentlySelected && !isSelected) {
          delete newZipcodes[zipcode];
        } else if (!isCurrentlySelected && isSelected) {
          const demogr = currentDemogrData.find(
            (demographic) => demographic.zip === zipcode,
          );
          if (!demogr) {
            return selectedZipcodes;
          } else {
            newZipcodes[zipcode] = demogr;
          }
        } else {
          return selectedZipcodes;
        }
        return newZipcodes;
      });
    },
    [currentDemogrData],
  );

  useEffect(() => {
    mapstore.initalizeMapStore();
    setDemographics({});
    setSelectedZipcodes({});
    setQuote(EMPTY_QUOTE);
    setMarketBboxes({
      '': {
        isLoaded: true,
        result: US_BBOX,
        limitZoom: false,
      },
    });
    handleSelectMarketInOverview(audience.markets[0]?.prodcode ?? '');
  }, [audience.selectedAudience.label]);

  useEffect(() => {
    const totalAddresses = Object.values(selectedZipcodes).reduce(
      (res, demogr) => res + demogr.total_households,
      0,
    );
    if (totalAddresses > ABDM_MIN_ADDRESSES) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [selectedZipcodes]);

  const bboxToZoom =
    prodcodeToZoom === undefined ? undefined : marketBboxes[prodcodeToZoom];

  return (
    <MapLayout>
      <ZipCodeSelectionWidget
        markets={newMarkets}
        allMarkets={audience.markets}
        selectedZipcodes={selectedZipcodes}
        demographics={demographics[prodcode]?.result ?? []}
        onSelectMarket={handleSelectMarket}
        quote={quote}
        bbox={bboxToZoom}
        onSelectMarketInOverview={handleSelectMarketInOverview}
        onSelectFeature={onSelectFeature}
      />
      <Divider />
      <Button
        disableElevation
        variant="contained"
        color={isDisabled ? 'secondary' : 'primary'}
        size="large"
        endIcon={<ChevronRightIcon />}
        onClick={isDisabled ? null : () => scrollToSection('delivery-date')}
      >
        Confirm market
      </Button>
    </MapLayout>
  );
};

export default observer(AreaSelector);
